// src/pages/gro/GroSettingsPage.tsx
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/shared/PageHeader";
import { GroSettingsForm } from "@/components/gro/GroSettingsForm";
import { useGroSettings, useUpdateGroSettings } from "@/hooks/useGroSettings";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type { GroSettingsUpdateIn } from "@/types/gro";

const emptyForm: GroSettingsUpdateIn = {
  risk_suggestion_mode: "suggest_only",
  action_plan_suggestion_mode: "suggest_only",
  inventory_mode: "continuous",
  allow_unit_analysis: true,
  allow_department_analysis: true,
  allow_job_position_analysis: true,
  allow_worker_group_analysis: true,
  min_respondents_for_aggregation: 5,
};

export default function GroSettingsPage() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.GRO_SETTINGS_WRITE) ?? false;

  const settingsQ = useGroSettings();
  const updateMut = useUpdateGroSettings();

  const [form, setForm] = useState<GroSettingsUpdateIn>(emptyForm);

  useEffect(() => {
    if (settingsQ.data) {
      setForm({
        risk_suggestion_mode: settingsQ.data.risk_suggestion_mode,
        action_plan_suggestion_mode: settingsQ.data.action_plan_suggestion_mode ?? "suggest_only",
        inventory_mode: settingsQ.data.inventory_mode,
        allow_unit_analysis: settingsQ.data.allow_unit_analysis,
        allow_department_analysis: settingsQ.data.allow_department_analysis,
        allow_job_position_analysis: settingsQ.data.allow_job_position_analysis,
        allow_worker_group_analysis: settingsQ.data.allow_worker_group_analysis,
        min_respondents_for_aggregation:
          settingsQ.data.min_respondents_for_aggregation,
      });
    }
  }, [settingsQ.data]);

  async function handleSave() {
    if (form.min_respondents_for_aggregation < 1) {
      toast.error("O mínimo de respondentes deve ser maior que zero.");
      return;
    }

    try {
      await updateMut.mutateAsync(form);
      toast.success("Configurações salvas com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar configurações.");
    }
  }

  return (
    <div>
      <PageHeader
        title="Configurações"
        description="Defina como o módulo psicossocial e de gestão de riscos deve funcionar."
      />

      {settingsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando configurações...
        </Card>
      ) : settingsQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(settingsQ.error as any)?.message || "Erro ao carregar configurações"}
          </div>
        </Card>
      ) : (
        <GroSettingsForm
          value={form}
          readOnly={!canWrite}
          saving={updateMut.isPending}
          onChange={setForm}
          onSubmit={handleSave}
        />
      )}
    </div>
  );
}
